import { useCallback, useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import type { SecurityActivityResponse } from "@lms/shared-types";
import { fetchJson } from "../../api/client";
import { useAuth } from "../../auth/AuthProvider";
import { credentialToJson, passkeysPossible, toWebAuthnCreationOptions, usableHere, type CognitoApi, type PasskeyRecord } from "../../auth/cognito-api";
import { useInvalidatePasskeys } from "../../auth/usePasskeys";
import { useStepUp } from "../../auth/StepUpProvider";
import { useCognitoApi } from "../../auth/useCognitoApi";
import { Button, Card, DataTable, EmptyState, ErrorState, LoadingState, RelativeTime, StatusPill, TextField } from "../../components/ui";
import { useBranding } from "../../lib/branding";
import { useFormat } from "../../lib/format";

/**
 * Account › Security (PLT-01, SEC-05; the auth template's Security
 * section on this app's primitives). Everything here is the browser
 * talking to Cognito with the person's own access token — no route on
 * our API can change how someone signs in. The one exception is the
 * activity view, which the API assembles from Cognito's history.
 */
/** The Security cards, as a section of the one Account page (profile template pitfall 9). */
export function SecuritySections() {
  const { session, config, me } = useAuth();
  const branding = useBranding();
  const api = useCognitoApi(config);
  const token = session?.accessToken;
  if (!token) return null;
  return (
    <>
      <p className="mb-4 text-muted">How you sign in, and where you have signed in from.</p>
      <div className="space-y-6">
        {me?.identityProvider ? (
          <Card title="Sign-in">
            <p className="text-muted">
              You sign in through <strong className="text-ink">{me.identityProvider}</strong>, your organization's identity provider. Your password, multi-factor authentication and passkeys are managed there, not here.
            </p>
          </Card>
        ) : (
          <>
            <Passkeys api={api} token={token} />
            <Authenticator api={api} token={token} productName={branding.productName} email={session?.email} />
            <PasswordAndSessions api={api} token={token} />
          </>
        )}
        <SecurityActivity />
      </div>
    </>
  );
}

export { usableHere };

function useCognitoList<T>(load: () => Promise<T>) {
  const [state, setState] = useState<{ data?: T; error?: unknown; loading: boolean }>({ loading: true });
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((n) => n + 1), []);
  useEffect(() => {
    let live = true;
    // The state changes here are answers from Cognito arriving later, not synchronous writes in the effect.
    load()
      .then((data) => live && setState({ data, loading: false }))
      .catch((error: unknown) => live && setState({ error, loading: false }));
    return () => {
      live = false;
    };
  }, [load, tick]);
  return { ...state, reload };
}

function Passkeys({ api, token }: { api: CognitoApi; token: string }) {
  const { ensureFresh } = useStepUp();
  const list = useCognitoList(useCallback(() => api.listPasskeys(token).then((r) => r.Credentials ?? []), [api, token]));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>();
  const supported = passkeysPossible();
  // The passkey nudge counts the same list; it follows an enrol or a removal at once (PLT-06).
  const invalidatePasskeys = useInvalidatePasskeys();

  const enrol = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await ensureFresh();
      const start = await api.startPasskey(token);
      const created = await navigator.credentials.create(toWebAuthnCreationOptions(start.CredentialCreationOptions));
      await api.completePasskey(token, credentialToJson(created));
      list.reload();
      invalidatePasskeys();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  const remove = async (c: PasskeyRecord) => {
    setBusy(true);
    setError(undefined);
    try {
      await ensureFresh();
      await api.deletePasskey(token, c.CredentialId);
      list.reload();
      invalidatePasskeys();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Passkeys">
      <p className="text-muted">A passkey signs you in with your fingerprint, face or security key instead of a password and code. Enroll one on each device you sign in from.</p>
      {!supported && <p className="mt-2 text-warn">This browser does not support passkeys.</p>}
      {list.loading && <LoadingState label="Loading passkeys…" />}
      {list.error !== undefined && <ErrorState title="Your passkeys could not be loaded." error={list.error} onRetry={() => list.reload()} />}
      {list.data && (
        <div className="mt-3">
          <DataTable
            caption="Your passkeys"
            captionHidden
            rows={list.data}
            rowKey={(c) => c.CredentialId}
            filter={false}
            empty={<EmptyState title="No passkeys yet" hint="Enroll one below; you keep your authenticator app as well." />}
            columns={[
              { key: "name", header: "Name", render: (c) => c.FriendlyCredentialName ?? "Passkey" },
              { key: "kind", header: "Kind", render: (c) => (c.AuthenticatorAttachment === "platform" ? "This device" : c.AuthenticatorAttachment === "cross-platform" ? "Security key" : "—") },
              // Cognito keeps the record when the relying-party id changes; the row must say the passkey is dead here.
              { key: "usable", header: "Usable here", render: (c) => (usableHere(c) ? <StatusPill tone="ok">Yes</StatusPill> : <StatusPill tone="warn">No — enrolled for {c.RelyingPartyId}; remove it and enroll again</StatusPill>) },
              { key: "since", header: "Enrolled", render: (c) => (c.CreatedAt ? <RelativeTime iso={new Date(c.CreatedAt * 1000).toISOString()} /> : "—") },
              { key: "act", header: "Action", render: (c) => <Button disabled={busy} onClick={() => void remove(c)}>Remove {c.FriendlyCredentialName ?? "passkey"}</Button> },
            ]}
          />
        </div>
      )}
      <Button variant="primary" className="mt-4" disabled={!supported || busy} onClick={() => void enrol()}>
        {busy ? "Working…" : "Enroll a passkey"}
      </Button>
      {error !== undefined && <ErrorState title="That did not work." error={error} />}
    </Card>
  );
}

function Authenticator({ api, token, productName, email }: { api: CognitoApi; token: string; productName: string; email?: string | undefined }) {
  const { ensureFresh } = useStepUp();
  const [secret, setSecret] = useState<string>();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<unknown>();
  // The TOTP label is the tenant's product name (ADR-0008 item 2), never a literal.
  const otpauth = secret ? `otpauth://totp/${encodeURIComponent(productName)}:${encodeURIComponent(email ?? "you")}?secret=${secret}&issuer=${encodeURIComponent(productName)}` : undefined;

  const begin = async () => {
    setBusy(true);
    setError(undefined);
    setDone(false);
    try {
      await ensureFresh();
      const r = await api.associateSoftwareToken(token);
      if (!r.SecretCode) throw new Error("Cognito returned no secret");
      setSecret(r.SecretCode);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  const verify = async () => {
    setBusy(true);
    setError(undefined);
    try {
      const r = await api.verifySoftwareToken(token, code, "Authenticator app");
      if (r.Status !== "SUCCESS") throw new Error("The code did not match; try the next one.");
      // Managed login's own MFA setup leaves the per-user preference unset (pitfall 5); enrolling here records it.
      await api.setTotpPreference(token);
      setSecret(undefined);
      setCode("");
      setDone(true);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Authenticator app">
      <p className="text-muted">Required for every password sign-in, so yours is set up. Moving to a new phone means enrolling it here; the old one stops working once the new one's first code is verified.</p>
      {!secret ? (
        <Button className="mt-3" disabled={busy} onClick={() => void begin()}>
          Enroll a new authenticator
        </Button>
      ) : (
        <form
          className="mt-3 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            void verify();
          }}
        >
          <p>Scan this with your authenticator app, then enter the code it shows.</p>
          {/* The code is always dark on white, whatever the theme: a phone camera needs that contrast and the quiet
              zone around it, and an inverted code fails on many scanners (owner, 2026-09-22). Beside it, the same
              secret in words — a QR code is no use to someone who cannot see it, and typing it is the same enrolment. */}
          <div className="flex flex-wrap items-start gap-4">
            <div className="shrink-0 rounded-lg border border-line bg-white p-3">
              <QRCodeSVG value={otpauth ?? ""} size={176} level="M" marginSize={2} bgColor="#ffffff" fgColor="#000000" role="img" title={`QR code that adds ${productName} to your authenticator app`} />
            </div>
            <div className="min-w-0 space-y-2">
              <p className="text-muted">Can't scan it? Type this code into the app instead.</p>
              <p className="break-all rounded bg-canvas p-2 font-mono text-sm" aria-label="Authenticator secret">
                {secret}
              </p>
              <a href={otpauth}>Open in an authenticator app on this device</a>
            </div>
          </div>
          <TextField label="Code from the app" inputMode="numeric" autoComplete="one-time-code" value={code} pattern="[0-9]{6}" required onChange={(e) => setCode(e.target.value)} className="max-w-xs" />
          <div className="flex flex-wrap gap-3">
            <Button type="submit" variant="primary" disabled={busy}>
              Verify and switch
            </Button>
            <Button onClick={() => setSecret(undefined)}>Cancel</Button>
          </div>
        </form>
      )}
      {done && (
        <p role="status" className="mt-2 text-ok">
          Your new authenticator is active.
        </p>
      )}
      {error !== undefined && <ErrorState title="That did not work." error={error} />}
    </Card>
  );
}

function PasswordAndSessions({ api, token }: { api: CognitoApi; token: string }) {
  const { signOut } = useAuth();
  const { ensureFresh } = useStepUp();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [again, setAgain] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>();
  const mismatch = again.length > 0 && next !== again;

  const change = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await ensureFresh();
      await api.changePassword(token, current, next);
      await api.globalSignOut(token);
      await signOut();
    } catch (e) {
      setError(e);
      setBusy(false);
    }
  };
  const everywhere = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await ensureFresh();
      await api.globalSignOut(token);
      await signOut();
    } catch (e) {
      setError(e);
      setBusy(false);
    }
  };

  return (
    <Card title="Password and sessions">
      <form
        className="grid gap-4 md:grid-cols-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (!mismatch) void change();
        }}
      >
        <TextField label="Current password" type="password" autoComplete="current-password" value={current} required onChange={(e) => setCurrent(e.target.value)} />
        <TextField label="New password" type="password" autoComplete="new-password" value={next} required minLength={14} description="At least 14 characters, with upper and lower case, a number and a symbol." onChange={(e) => setNext(e.target.value)} />
        <TextField label="New password again" type="password" autoComplete="new-password" value={again} required error={mismatch ? "The two do not match." : undefined} onChange={(e) => setAgain(e.target.value)} />
        <div className="flex flex-wrap items-center gap-3 md:col-span-3">
          <Button type="submit" variant="primary" disabled={busy || mismatch || !current || !next}>
            {busy ? "Working…" : "Change password"}
          </Button>
          <span className="text-sm text-muted">Changing it signs every session out, including this one; you sign in again with the new password.</span>
        </div>
      </form>
      <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-line pt-4">
        <Button disabled={busy} onClick={() => void everywhere()}>
          Sign out everywhere
        </Button>
        <span className="text-sm text-muted">Cognito cannot list where you are signed in, so this signs out every session — this one too.</span>
      </div>
      {error !== undefined && <ErrorState title="That did not work." error={error} />}
    </Card>
  );
}

function SecurityActivity() {
  const { config, session } = useAuth();
  const fmt = useFormat();
  const [data, setData] = useState<SecurityActivityResponse>();
  const [error, setError] = useState<unknown>();
  const load = useCallback(() => (session ? fetchJson<SecurityActivityResponse>(config, session, "/v1/me/security-activity").then(setData).catch(setError) : Promise.resolve()), [config, session]);
  useEffect(() => {
    void load();
  }, [load]);
  return (
    <Card title="Security activity">
      <p className="text-muted">Recent sign-ins to your account. If one is not yours, sign out everywhere and change your password.</p>
      {!data && error === undefined && <LoadingState label="Loading your activity…" />}
      {error !== undefined && <ErrorState title="Your activity could not be loaded." error={error} onRetry={() => void load()} />}
      {data && (
        <>
          {!data.signInsAvailable && <p className="mt-2 text-muted">Sign-in history is not available on this deployment.</p>}
          <div className="mt-3">
            <DataTable
              caption="Sign-ins"
              captionHidden
              rows={data.signIns}
              rowKey={(e) => e.id}
              filter={false}
              empty={<EmptyState title={data.signInsAvailable ? "No sign-ins recorded yet" : "Nothing to show"} />}
              columns={[
                { key: "when", header: "When", render: (e) => fmt.when(e.at) },
                { key: "type", header: "Event", render: (e) => e.type },
                { key: "outcome", header: "Outcome", render: (e) => <StatusPill tone={e.outcome === "pass" ? "ok" : e.outcome === "fail" ? "danger" : "neutral"}>{e.outcome.replace("_", " ")}</StatusPill> },
                { key: "where", header: "From", render: (e) => [e.ip, e.place].filter(Boolean).join(" · ") || "—" },
                { key: "device", header: "Device", render: (e) => e.device ?? "—" },
              ]}
            />
          </div>
        </>
      )}
    </Card>
  );
}
